import { useMutation, type UseMutationResult } from '@tanstack/react-query'
import type { OtpFetchResponse } from '@acs/shared'
import { apiPost } from '@/lib/api'

/**
 * Reads the newest Amazon verification code out of one customer's Gmail.
 *
 * The cheap counterpart to starting a run: no browser, nothing sent to Amazon,
 * no new code triggered — the server opens the mailbox read-only with the app
 * password already on the record, scans it once and closes. So pressing the
 * button again is free, and the two rules below are about connections rather
 * than side effects.
 *
 * `retry: false`: a failed read is either a mailbox that refused the app
 * password (replaying it locks the account faster) or one that could not be
 * reached (the server has already spent its own budget trying). Neither is
 * improved by a second automatic attempt, and both would stack another IMAP
 * connection against Gmail's per-account cap. The operator retries, deliberately.
 */
const READ_TIMEOUT_MS = 30_000

export function useFetchOtp(): UseMutationResult<OtpFetchResponse, unknown, string> {
  return useMutation<OtpFetchResponse, unknown, string>({
    mutationFn: (id: string) =>
      apiPost<OtpFetchResponse>(
        `/api/customers/${encodeURIComponent(id)}/otp`,
        {},
        undefined,
        /*
         * Longer than the server's own ceiling (20s of connect-and-scan), and
         * that ordering is load-bearing: a client that gives up first reports
         * "could not reach the server" for a read that was about to answer, and
         * the operator presses the button again — one more IMAP login against a
         * mailbox that was merely slow.
         */
        READ_TIMEOUT_MS
      ),
    retry: false,
  })
}
